import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useTheme } from '../theme';

interface GoogleIdentity {
  initialize: (options: { client_id: string; login_uri: string; ux_mode: 'redirect'; nonce: string; auto_select: false; context: 'signin' }) => void;
  renderButton: (container: HTMLElement, options: { type: 'standard'; theme: 'outline' | 'filled_black'; size: 'large'; text: 'continue_with'; shape: 'rectangular'; width: number; logo_alignment: 'left' }) => void;
}
declare global { interface Window { google?: { accounts: { id: GoogleIdentity } } } }

let library: Promise<GoogleIdentity> | null = null;
function loadGoogleIdentity(): Promise<GoogleIdentity> {
  if (window.google?.accounts.id) return Promise.resolve(window.google.accounts.id);
  if (library) return library;
  library = new Promise<GoogleIdentity>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    const fail = () => { clearTimeout(timeout); script.remove(); library = null; reject(new Error('Google sign-in is unavailable')); };
    const timeout = window.setTimeout(fail, 10_000);
    script.onerror = fail;
    script.onload = () => {
      if (!window.google?.accounts.id) return fail();
      clearTimeout(timeout);
      resolve(window.google.accounts.id);
    };
    document.head.appendChild(script);
  });
  return library;
}

export function GoogleSignInButton({ clientId, loginUri, invitation, prepare }: {
  clientId: string; loginUri: string; invitation?: string;
  /** How this surface starts a flow. The account portal and a workspace each
   * have their own endpoint; the button itself is the same. */
  prepare?: (returnPath: string) => Promise<{ nonce: string }>;
}) {
  const container = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  // Callers usually pass an inline arrow, which is a new function on every
  // render of their page. Held in a ref so a parent re-rendering (somebody
  // typing in the form below) cannot tear the button down and build it again.
  const prepareRef = useRef(prepare);
  useEffect(() => { prepareRef.current = prepare; }, [prepare]);

  useEffect(() => {
    let cancelled = false;
    let observer: ResizeObserver | undefined;
    let expiry: number | undefined;
    const element = container.current!;
    element.replaceChildren();
    setState('loading');
    void (async () => {
      try {
        const identity = await loadGoogleIdentity();
        if (cancelled) return;
        const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const start = prepareRef.current;
        const { nonce } = await (start ? start(path) : api.prepareGoogleSignIn(path, invitation));
        if (cancelled) return;
        identity.initialize({ client_id: clientId, login_uri: loginUri, ux_mode: 'redirect', nonce, auto_select: false, context: 'signin' });
        let previousWidth = 0;
        const render = () => {
          const width = Math.max(200, Math.min(400, Math.floor(element.clientWidth)));
          if (width === previousWidth) return;
          previousWidth = width;
          element.replaceChildren();
          identity.renderButton(element, { type: 'standard', theme: theme === 'dark' ? 'filled_black' : 'outline', size: 'large', text: 'continue_with', shape: 'rectangular', width, logo_alignment: 'left' });
        };
        render();
        observer = new ResizeObserver(render);
        observer.observe(element);
        setState('ready');
        // Refresh the browser-bound nonce before its ten-minute lifetime ends.
        expiry = window.setTimeout(() => setAttempt(value => value + 1), 9 * 60_000);
      } catch { if (!cancelled) setState('failed'); }
    })();
    return () => { cancelled = true; observer?.disconnect(); clearTimeout(expiry); element.replaceChildren(); };
  }, [clientId, loginUri, invitation, theme, attempt]);

  return <div className="google-signin">
    {/* The slot keeps the button's height whether or not Google has answered
        yet, so nothing below it moves when the status message gives way. */}
    {state !== 'failed' && <div className="google-signin-slot">
      <div ref={container} className="google-signin-button" />
      {state === 'loading' && <p className="google-signin-status" role="status">Loading Google sign-in…</p>}
    </div>}
    {state === 'failed' && <div className="google-signin-failure" role="status">
      <p>Google sign-in couldn’t load. You can still use your email and password.</p>
      <button type="button" className="btn btn-ghost" onClick={() => setAttempt(value => value + 1)}>Retry Google sign-in</button>
    </div>}
  </div>;
}
